import React, { useCallback, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applySeo } from '../../utils/seo';
import { ogBrandImageUrl } from '../../constants/ogBrand';
import { normalizeUiLang } from '../../utils/localePreference';
import { digitalCardStyles } from './digitalCardStyles';
import DigitalCardNav from './DigitalCardNav';
import DigitalCardFooter from './DigitalCardFooter';
import TemplatesGallerySection from './TemplatesGallerySection';
import { TEMPLATE_GRID_ID } from './digitalCardRoutes';
import type { TemplateCategory } from '../../hooks/usePublicTemplates';

const TemplatesGalleryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const resolvedLang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const location = useLocation();
  const { category } = useParams<{ category?: string }>();

  const activeCategory: TemplateCategory | 'all' =
    category === 'business' ? 'business' : category === 'artist' ? 'artist' : 'all';

  useEffect(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.tcards.org';
    const path = category ? `/templates/${category}` : '/templates';
    const dispose = applySeo({
      title: t('dcl_templates_page_title', { defaultValue: 'Choose Your TCard | TCards Templates' }),
      description: t('dcl_templates_page_desc', {
        defaultValue:
          'Browse Business and Artist TCard templates. Pick a design that matches your style, then personalize your digital card in minutes.',
      }),
      image: ogBrandImageUrl(origin, 'site'),
      url: `${origin}${path}`,
      canonicalUrl: `${origin}${path}`,
      siteName: 'TCards',
    });
    return dispose;
  }, [t, category, resolvedLang]);

  const scrollToTemplateGrid = useCallback(() => {
    document.getElementById(TEMPLATE_GRID_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (location.hash === `#${TEMPLATE_GRID_ID}`) {
      const timer = window.setTimeout(scrollToTemplateGrid, 50);
      return () => window.clearTimeout(timer);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, category, scrollToTemplateGrid]);

  const categories: { key: TemplateCategory | 'all'; label: string; to: string }[] = [
    { key: 'all', label: t('dcl_filter_all_templates', { defaultValue: 'All templates' }), to: '/templates' },
    { key: 'business', label: t('dcl_cat_business', { defaultValue: 'Business Templates' }), to: '/templates/business' },
    { key: 'artist', label: t('dcl_cat_artist', { defaultValue: 'Artist Templates' }), to: '/templates/artist' },
  ];

  return (
    <div className="dcl-root dcl-root--linktree dcl-root--templates">
      <style>{digitalCardStyles}</style>
      <DigitalCardNav activePath="/templates" />

      {/* Hero — Linktree templates style */}
      <section className="dcl-tpl-hero">
        <div className="dcl-container dcl-container--narrow">
          <h1 className="dcl-tpl-hero-title">
            {t('dcl_templates_lt_heading', {
              defaultValue: 'A TCard template to suit every professional and creator',
            })}
          </h1>
          <p className="dcl-tpl-hero-sub">
            {t('dcl_templates_lt_sub', {
              defaultValue:
                'Different layouts, styles and categories help you create a TCard that looks and feels like you. Explore our library of Business and Artist templates to present your identity beautifully.',
            })}
          </p>
        </div>
      </section>

      {/* Category navigation */}
      <nav className="dcl-tpl-nav" aria-label={t('dcl_tpl_nav_label', { defaultValue: 'Template categories' })}>
        <div className="dcl-container">
          <p className="dcl-tpl-nav-label">{t('dcl_tpl_browse_by', { defaultValue: 'Browse by category' })}</p>
          <div className="dcl-tpl-nav-scroll">
            {categories.map((c) => (
              <Link
                key={c.key}
                to={c.to}
                className={`dcl-tpl-nav-link${activeCategory === c.key ? ' dcl-tpl-nav-link--active' : ''}`}
                aria-current={activeCategory === c.key ? 'page' : undefined}
              >
                {c.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Template grid + detail */}
      <section id={TEMPLATE_GRID_ID} className="dcl-tpl-body dcl-scroll-target">
        <div className="dcl-container">
          <TemplatesGallerySection
            category={activeCategory}
            layout="linktree"
            showExplore={false}
            enableDetailPanel
          />
        </div>
      </section>

      {/* Final CTA */}
      <section className="dcl-section dcl-final-lt">
        <div className="dcl-container dcl-container--narrow">
          <h2 className="dcl-final-lt-title">
            {t('dcl_tpl_final_title', { defaultValue: 'Jumpstart your digital identity today' })}
          </h2>
          <p className="dcl-final-lt-sub">
            {t('dcl_tpl_final_sub', {
              defaultValue: 'Pick a template above, personalize your TCard, and share one beautiful link everywhere you connect.',
            })}
          </p>
          <button
            type="button"
            className="dcl-btn dcl-btn--dark dcl-btn--pill dcl-btn--lg"
            onClick={scrollToTemplateGrid}
          >
            {t('dcl_nav_get_started', { defaultValue: 'Get Started' })}
          </button>
        </div>
      </section>

      <DigitalCardFooter />
    </div>
  );
};

export default TemplatesGalleryPage;
